import { __decorate } from 'tslib';
import * as i0 from '@angular/core';
import { InjectionToken, inject, ElementRef, ChangeDetectorRef, Component, ChangeDetectionStrategy } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { tuiPure } from '@taiga-ui/cdk';
import { TUI_EDITOR_RESIZE_EVENT } from '@taiga-ui/editor/common';
import { AbstractTuiEditorResizable, TuiEditorResizable } from '@taiga-ui/editor/components/editor-resizable';
import { TuiNodeView } from '@taiga-ui/editor/extensions/tiptap-node-view';
import { Node, mergeAttributes } from '@tiptap/core';

/**
 * Size of resizable iframe inside editor
 */
const TUI_IFRAME_EDITOR_OPTIONS = new InjectionToken('[TUI_IFRAME_EDITOR_OPTIONS]', {
  factory: () => ({
    minWidth: 100,
    maxWidth: Infinity,
    minHeight: 100,
    maxHeight: Infinity
  })
});
let TuiIframeEditor = /*#__PURE__*/(() => {
  class TuiIframeEditor extends AbstractTuiEditorResizable {
    constructor() {
      super(...arguments);
      this.sanitizer = inject(DomSanitizer);
      this.el = inject(ElementRef);
      this.options = inject(TUI_IFRAME_EDITOR_OPTIONS);
      this.changeDetector = inject(ChangeDetectorRef);
    }
    updateSize([width, height]) {
      this.currentWidth = Math.max(this.options.minWidth, Math.min(this.options.maxWidth, width));
      this.currentHeight = Math.max(this.options.minHeight, Math.min(this.options.maxHeight, height));
      this.attrs.width = this.currentWidth;
      this.attrs.height = this.currentHeight;
      this.el.nativeElement.dispatchEvent(new CustomEvent(TUI_EDITOR_RESIZE_EVENT, {
        bubbles: true
      }));
    }
    get src() {
      return this.sanitizer.bypassSecurityTrustResourceUrl(this.attrs.src ?? '');
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵTuiIframeEditor_BaseFactory;
        return function TuiIframeEditor_Factory(__ngFactoryType__) {
          return (ɵTuiIframeEditor_BaseFactory || (ɵTuiIframeEditor_BaseFactory = i0.ɵɵgetInheritedFactory(TuiIframeEditor)))(__ngFactoryType__ || TuiIframeEditor);
        };
      })();
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: TuiIframeEditor,
        selectors: [["tui-iframe-editor"]],
        features: [i0.ɵɵInheritDefinitionFeature],
        decls: 2,
        vars: 13,
        consts: [[3, "sizeChange", "height", "width"], ["alt", "", 3, "height", "src", "width"]],
        template: function TuiIframeEditor_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelementStart(0, "tui-editor-resizable", 0);
            i0.ɵɵlistener("sizeChange", function TuiIframeEditor_Template_tui_editor_resizable_sizeChange_0_listener($event) {
              return ctx.updateSize($event);
            });
            i0.ɵɵelement(1, "iframe", 1);
            i0.ɵɵelementEnd();
          }
          if (rf & 2) {
            i0.ɵɵproperty("height", ctx.height)("width", ctx.width);
            i0.ɵɵadvance();
            i0.ɵɵstyleProp("max-height", ctx.options.maxHeight)("max-width", ctx.options.maxWidth)("min-height", ctx.options.minHeight)("min-width", ctx.options.minWidth);
            i0.ɵɵproperty("height", ctx.height)("src", ctx.src, i0.ɵɵsanitizeResourceUrl)("width", ctx.width);
          }
        },
        dependencies: [TuiEditorResizable],
        styles: ["iframe[_ngcontent-%COMP%]{display:inline;pointer-events:none}"],
        changeDetection: 0
      });
    }
  }
  __decorate([tuiPure], TuiIframeEditor.prototype, "src", null);
  return TuiIframeEditor;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const tuiCreateIframeEditorExtension = ({
  injector
}) => Node.create({
  name: 'iframe',
  group: 'block',
  atom: true,
  draggable: false,
  parseHTML() {
    return [{
      tag: 'iframe'
    }];
  },
  addAttributes() {
    return {
      src: {
        default: null,
        keepOnSplit: false,
        parseHTML: element => element.getAttribute('src')
      },
      frameborder: {
        default: 0,
        keepOnSplit: false,
        parseHTML: element => element.getAttribute('frameborder')
      },
      width: {
        default: '100%',
        keepOnSplit: false,
        parseHTML: element => element.getAttribute('width')
      },
      height: {
        default: null,
        keepOnSplit: false,
        parseHTML: element => element.getAttribute('height')
      },
      allowfullscreen: {
        keepOnSplit: false,
        default: this.options.allowFullscreen,
        parseHTML: element => element.getAttribute('allowfullscreen')
      }
    };
  },
  renderHTML({
    HTMLAttributes
  }) {
    return ['iframe', mergeAttributes(HTMLAttributes, {
      'data-type': 'iframe-editor'
    })];
  },
  addNodeView() {
    return props => new TuiNodeView(TuiIframeEditor, props, {
      injector,
      ...props
    });
  },
  addCommands() {
    return {
      setIframe: attrs => ({
        commands,
        state
      }) => {
        const prevLine = state.selection.anchor;
        commands.enter();
        commands.setTextSelection(prevLine);
        commands.insertContent({
          type: this.name,
          attrs
        });
        commands.setTextSelection(state.selection.anchor);
        return true;
      }
    };
  }
});

/**
 * Generated bundle index. Do not edit.
 */

export { TUI_IFRAME_EDITOR_OPTIONS, TuiIframeEditor, tuiCreateIframeEditorExtension };
